/**
 * As unidades do motor CPTu e o nome de cada grandeza.
 *
 * Dentro do motor tudo anda nestas unidades (RN-252), sem conversão escondida:
 *
 *   profundidade z .......... m
 *   qc, qt .................. MPa
 *   fs, u₂, u₀, σv, σ'v, Su . kPa
 *   γ ....................... kN/m³
 *   G₀, M, Es ............... MPa
 *   k ....................... m/s
 *   cₕ ...................... m²/s
 *   Vs ...................... m/s
 *   ângulos ................. graus
 *   Rf, Fr, Dr, w ........... %
 *
 * «Não se aplica» é NaN (nunca 0, nunca −1, nunca exceção).
 */
import type { Grandeza } from './tipos';

/** Pressão atmosférica de referência das normalizações (Cn, Qtn, N60), em kPa. */
export const PA_KPA = 101.325;

/** Peso específico da água, em kN/m³. */
export const GAMA_W_PADRAO = 9.81;

/** O rótulo (português), a unidade e as casas de uma grandeza. */
export interface DescricaoDaGrandeza {
  rotulo: string;
  unidade: string;
  casas: number;
}

/**
 * Rótulo (português), unidade e casas de cada grandeza — as casas que o
 * CPeT-IT exporta, e portanto as da tabela e da planilha do relatório.
 *
 * Unidade vazia é grandeza adimensional. Em `k` e `ch` (notação científica) as
 * casas são as da mantissa: o CPeT-IT exporta 3 algarismos significativos
 * (9,88e-9; 1,63e-6), isto é, 2 casas na mantissa — 4,36×10⁻⁸.
 *
 * O `ch` é o cₕ do PERFIL, k·M/γw (a coluna «Ch» da aba «Estimations»), e o
 * rótulo diz isso: o cₕ da dissipação (Teh & Houlsby, `dissipacao.ts`) é outra
 * grandeza, com outra homologação (`dissipacaoHomologada`, `conformidade.ts`).
 *
 * Congelada, e cada linha também: ninguém muda um rótulo ou as casas por engano.
 */
export const GRANDEZAS: Readonly<Record<Grandeza, Readonly<DescricaoDaGrandeza>>> = congelar({
  z: { rotulo: 'Profundidade', unidade: 'm', casas: 2 },
  qc: { rotulo: 'qc', unidade: 'MPa', casas: 2 },
  fs: { rotulo: 'fs', unidade: 'kPa', casas: 2 },
  u2: { rotulo: 'u₂', unidade: 'kPa', casas: 2 },
  qt: { rotulo: 'qt', unidade: 'MPa', casas: 2 },
  rf: { rotulo: 'Rf', unidade: '%', casas: 2 },
  gama: { rotulo: 'γ', unidade: 'kN/m³', casas: 2 },
  sv: { rotulo: 'σv', unidade: 'kPa', casas: 2 },
  u0: { rotulo: 'u₀', unidade: 'kPa', casas: 2 },
  svl: { rotulo: "σ'v", unidade: 'kPa', casas: 2 },
  qt1: { rotulo: 'Qt1', unidade: '', casas: 2 },
  fr: { rotulo: 'Fr', unidade: '%', casas: 2 },
  bq: { rotulo: 'Bq', unidade: '', casas: 2 },
  n: { rotulo: 'n', unidade: '', casas: 2 },
  cn: { rotulo: 'Cn', unidade: '', casas: 2 },
  ic: { rotulo: 'Ic', unidade: '', casas: 2 },
  qtn: { rotulo: 'Qtn', unidade: '', casas: 2 },
  icSbt: { rotulo: 'Ic (SBT)', unidade: '', casas: 2 },
  U2: { rotulo: 'U₂', unidade: '', casas: 2 },
  ib: { rotulo: 'I(B)', unidade: '', casas: 2 },
  k: { rotulo: 'k', unidade: 'm/s', casas: 2 },
  ch: { rotulo: 'cₕ (perfil, k·M/γw)', unidade: 'm²/s', casas: 2 },
  n60: { rotulo: 'N60', unidade: 'golpes/30 cm', casas: 0 },
  n160: { rotulo: 'N1(60)', unidade: 'golpes/30 cm', casas: 0 },
  m: { rotulo: 'M', unidade: 'MPa', casas: 2 },
  dr: { rotulo: 'Dr', unidade: '%', casas: 2 },
  phi: { rotulo: "φ'", unidade: '°', casas: 2 },
  es: { rotulo: 'Es', unidade: 'MPa', casas: 2 },
  g0: { rotulo: 'G₀', unidade: 'MPa', casas: 2 },
  nkt: { rotulo: 'Nkt', unidade: '', casas: 0 },
  su: { rotulo: 'Su', unidade: 'kPa', casas: 2 },
  suRazao: { rotulo: "Su/σ'v", unidade: '', casas: 2 },
  kocr: { rotulo: 'kOCR', unidade: '', casas: 2 },
  ocr: { rotulo: 'OCR', unidade: '', casas: 2 },
  vs: { rotulo: 'Vs', unidade: 'm/s', casas: 2 },
  psi: { rotulo: 'ψ', unidade: '', casas: 2 },
  k0: { rotulo: 'K₀', unidade: '', casas: 2 },
  st: { rotulo: 'St', unidade: '', casas: 2 },
  phiPico: { rotulo: "φ' de pico", unidade: '°', casas: 2 },
  resistencia: { rotulo: 'Resistência ao cisalhamento', unidade: 'kPa', casas: 2 },
  kc: { rotulo: 'Kc', unidade: '', casas: 2 },
  qtncs: { rotulo: 'Qtn,cs', unidade: '', casas: 2 },
  // A coluna «Residual Shear Strength» do CPeT-IT é esta RAZÃO, a resistência liquefeita
  // su(liq)/σ'v de Robertson (2022) — não o Su(rem) = fs em kPa da folha (`correlacoes.ts`,
  // escolha 14). Adimensional: unidade vazia, como Su/σ'v.
  suR: { rotulo: "Su(liq)/σ'v", unidade: '', casas: 2 },
  w: { rotulo: 'w', unidade: '%', casas: 2 },
  e: { rotulo: 'e', unidade: '', casas: 2 },
  gamaD: { rotulo: 'γd', unidade: 'kN/m³', casas: 2 },
  porosidade: { rotulo: 'n (porosidade)', unidade: '', casas: 2 },
});

/** Congela a tabela e cada linha dela. */
function congelar<T extends object>(tabela: T): Readonly<T> {
  for (const linha of Object.values(tabela)) Object.freeze(linha);
  return Object.freeze(tabela);
}
